import {
  BarChart3,
  Download,
  Filter,
  Calendar,
  RefreshCw,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import { useProjects, type Project, useTeams, type Team, useExportReport, type AnalyticsFilters } from '@/hooks'
import { cn } from '@/lib/utils'

import { CompletionTrendChart } from './completion-trend-chart'
import { EstimationAccuracyChart } from './estimation-accuracy-chart'
import { RollbackRateChart } from './rollback-rate-chart'
import { StageStatisticsChart } from './stage-statistics-chart'
import { TeamEfficiencyChart } from './team-efficiency-chart'

interface WorkflowAnalyticsDashboardProps {
  className?: string
}

/**
 * 工作流分析仪表板
 * Requirements: 3.6, 3.9
 */
export function WorkflowAnalyticsDashboard({ className }: WorkflowAnalyticsDashboardProps) {
  const [filters, setFilters] = useState<AnalyticsFilters>({})
  const [trendDays, setTrendDays] = useState(30)
  const [isExporting, setIsExporting] = useState(false)
  const toast = useToast()

  const { data: projectsData } = useProjects({ pageSize: 100 })
  const { data: teamsData } = useTeams({ pageSize: 100 })

  const exportReport = useExportReport()

  // useProjects and useTeams return arrays directly
  const projects: Project[] = Array.isArray(projectsData) ? projectsData : []
  const teams: Team[] = Array.isArray(teamsData) ? teamsData : []

  const handleExport = async (format: 'csv' | 'json') => {
    setIsExporting(true)
    try {
      const report = await exportReport(filters, format)

      // 创建下载
      const blob = new Blob([report], {
        type: format === 'csv' ? 'text/csv' : 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `workflow-analytics-${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('导出成功')
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('导出失败', error instanceof Error ? error.message : '请稍后重试')
    } finally {
      setIsExporting(false)
    }
  }

  const handleClearFilters = () => {
    setFilters({})
  }

  const hasFilters = filters.projectId || filters.teamId || filters.startDate || filters.endDate

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-6 w-6" />
          <h2 className="text-xl font-semibold">工作流分析</h2>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExport('csv')}
            disabled={isExporting}
          >
            <Download className="h-4 w-4 mr-2" />
            导出 CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExport('json')}
            disabled={isExporting}
          >
            <Download className="h-4 w-4 mr-2" />
            导出 JSON
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/50 rounded-lg">
        <Filter className="h-4 w-4 text-muted-foreground" />

        <Select
          value={filters.projectId ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({ ...f, projectId: v === 'all' ? undefined : v }))
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="选择项目" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部项目</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.teamId ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({ ...f, teamId: v === 'all' ? undefined : v }))
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="选择团队" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部团队</SelectItem>
            {teams.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Input
            type="date"
            value={filters.startDate ?? ''}
            onChange={(e) =>
              setFilters((f) => ({ ...f, startDate: e.target.value || undefined }))
            }
            className="w-[140px]"
            placeholder="开始日期"
          />
          <span className="text-muted-foreground">-</span>
          <Input
            type="date"
            value={filters.endDate ?? ''}
            onChange={(e) =>
              setFilters((f) => ({ ...f, endDate: e.target.value || undefined }))
            }
            className="w-[140px]"
            placeholder="结束日期"
          />
        </div>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <RefreshCw className="h-4 w-4 mr-1" />
            清除筛选
          </Button>
        )}
      </div>

      {/* Charts Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* 阶段耗时统计 */}
        <StageStatisticsChart filters={filters} />

        {/* 回退率统计 */}
        <RollbackRateChart filters={filters} />

        {/* 估算准确率 */}
        <EstimationAccuracyChart filters={filters} />

        {/* 团队效率排名 */}
        <TeamEfficiencyChart filters={filters} />
      </div>

      {/* 完成率趋势 - 全宽 */}
      <div className="flex items-center gap-4 mb-2">
        <span className="text-sm text-muted-foreground">趋势周期:</span>
        <Select
          value={String(trendDays)}
          onValueChange={(v) => setTrendDays(Number(v))}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">最近 7 天</SelectItem>
            <SelectItem value="14">最近 14 天</SelectItem>
            <SelectItem value="30">最近 30 天</SelectItem>
            <SelectItem value="60">最近 60 天</SelectItem>
            <SelectItem value="90">最近 90 天</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <CompletionTrendChart filters={filters} days={trendDays} />
    </div>
  )
}
